import { isNumber } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';
import { type IVectorBorderRadiusProps, type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { AutoDragModal } from '@/panels/image-layer-panel/components/effect-panel/views/auto-drag-model/auto-drag-model';
import { BorderRadiusInput } from '../../components';
import { VectorPanelContext } from '../../context';
import { Tracker } from '../../utils';

const FALLBACK_BORDER_RADIUS: IVectorBorderRadiusProps = {
    leftTop: 0,
    rightTop: 0,
    leftBottom: 0,
    rightBottom: 0,
};

export const BorderRadiusController: React.FC = () => {
    const { layerId, isIntl, recordId } = useContext(VectorPanelContext)!;

    const { $t } = useLocaleStore();

    const editor = useEditor();
    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as IVectorLayerProps;
        const { borderRadius } = layerData;

        return {
            layerWidth,
            layerHeight,
            borderRadius,
        };
    });

    const layerWidth = props ? props.layerWidth : 0;
    const layerHeight = props ? props.layerHeight : 0;
    const borderRadius = props ? props.borderRadius || FALLBACK_BORDER_RADIUS : FALLBACK_BORDER_RADIUS;

    const maxValue = Math.ceil(Math.min(layerWidth, layerHeight) / 2);

    const containerRef = useRef<HTMLDivElement>(null);

    const allRadiusSame = useMemo(() => {
        const allRadius = Object.values(borderRadius);
        return new Set(allRadius).size === 1;
    }, [borderRadius]);

    const radius = useMemo(() => {
        return allRadiusSame ? borderRadius.leftTop : 0;
    }, [allRadiusSame, borderRadius.leftTop]);

    const setCommonRadius = (value: number, options = { writeHistory: true }) => {
        if (!isNumber(value)) return;

        editor.getInstance(VectorLayer).setBorderRadius(layerId, value, options);

        if (options.writeHistory) {
            Tracker.setRadius({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const setCornerRadius = (
        corner: keyof IVectorBorderRadiusProps,
        value: number,
        options = { writeHistory: true },
    ) => {
        if (!isNumber(value)) return;

        editor.getInstance(VectorLayer).setBorderRadius(layerId, value, { corner, ...options });

        if (options.writeHistory) {
            Tracker.setRadius({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const closeBorderRadiusPopover = () => {
        AutoDragModal.close();
    };

    const openBorderRadiusPopover = () => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        AutoDragModal.show({
            children: (
                <BorderRadiusInput
                    borderRadius={borderRadius}
                    maxValue={maxValue}
                    onClose={closeBorderRadiusPopover}
                    onChange={(corner, value) => setCornerRadius(corner, value, { writeHistory: false })}
                    onAfterChange={(corner, value) => setCornerRadius(corner, value, { writeHistory: true })}
                />
            ),
            initPosition: {
                x: rect.x - 267,
                y: rect.y < 0 ? 0 : rect.y,
            },
            windowSize: {
                width: 250,
                height: 148,
            },
            className: 'vector-panel-border-radius-popover',
            bgCanOperate: true,
        });
    };

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // 检查点击的元素或其父元素是否包含指定类名
            const isBorderRadiusInput = target.closest('.border-radius-input');
            const isRadiusIcon = target.closest('.vector-panel-controller__icon-radius');

            if (!isBorderRadiusInput && !isRadiusIcon) {
                AutoDragModal.close();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            AutoDragModal.close();
        };
    }, []);

    return (
        <div className="vector-panel-controller" ref={containerRef}>
            <LabelAction title={$t((v) => v.panel.vectorPanel.radius)} align>
                <div style={{ width: isIntl ? 114 : 144 }}>
                    <SliderInput
                        showStepBtn
                        type="horizontal"
                        value={radius}
                        min={0}
                        max={maxValue}
                        onChange={(value) => setCommonRadius(value, { writeHistory: false })}
                        onAfterChange={(value) => setCommonRadius(value, { writeHistory: true })}
                    />
                </div>
                <div className="vector-panel-controller__icon-radius" onClick={openBorderRadiusPopover}>
                    <IconFont
                        name="AmplificationBold"
                        fontSize={14}
                        color={allRadiusSame ? 'var(--content-btn-transparency)' : 'var(--content-system-link)'}
                    />
                </div>
            </LabelAction>
        </div>
    );
};
